import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarPlus, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { ShopStorefrontType } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthenticatedMe } from "@/features/organizers/api";
import { useShopQuery, useUpdateShopMutation } from "@/features/shops/api";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const ONBOARDED_FLAG = "tn_onboarded";

export function isOnboarded(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(ONBOARDED_FLAG) === "true";
}

export function markOnboarded(): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ONBOARDED_FLAG, "true");
}

type Step = 1 | 2 | 3;

const STEP_KEYS: Record<Step, "type" | "confirm" | "done"> = { 1: "type", 2: "confirm", 3: "done" };
const STOREFRONT_TYPES: readonly ShopStorefrontType[] = ["Link", "Embed", "Site"];

/**
 * Yeni organizer için 3 adımlı kurulum sihirbazı.
 *
 * Backend signup zaten default shop oluşturuyor → wizard sadece tip'i ve isim
 * doğrulamasını yapıp organizeri ilk event ekleme akışına yönlendirir. Karmaşık
 * flag'lere gerek yok — localStorage ile tek seferlik gösterilir; tekrar açmak
 * istenirse `/onboarding` URL'i her zaman erişilebilir.
 */
export function OnboardingPage() {
  const t = useT("onboarding");
  const tCommon = useT("common");
  const navigate = useNavigate();
  const me = useAuthenticatedMe();
  const [step, setStep] = useState<Step>(1);
  const [storefrontType, setStorefrontType] = useState<ShopStorefrontType>("Link");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // /me sadece slim ShopSummary döner; tam alanlar için /shops/{slug} detay
  // sorgusunu yapıyoruz (PUT round-trip için tüm alanlar zorunlu).
  const defaultShopSummary = me.data?.shops[0] ?? null;
  const shopDetail = useShopQuery(defaultShopSummary?.slug ?? "");
  const fullShop = shopDetail.data ?? null;
  const updateShop = useUpdateShopMutation(defaultShopSummary?.slug ?? "");

  const [syncedType, setSyncedType] = useState<ShopStorefrontType | null>(null);
  if (fullShop?.storefrontType && fullShop.storefrontType !== syncedType) {
    setSyncedType(fullShop.storefrontType);
    setStorefrontType(fullShop.storefrontType);
  }

  if (me.isLoading || (defaultShopSummary && shopDetail.isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!me.data || !defaultShopSummary || !fullShop) return null;

  async function handleConfirm() {
    if (!fullShop) return;
    setIsSubmitting(true);
    try {
      // Backend UpdateShopRequest'te `name` zorunlu — diğer alanlar optional ama
      // round-trip etmek güvenli (kayıp önler). storefrontType wizard'ın asıl
      // değişikliği.
      await updateShop.mutateAsync({
        name: fullShop.name,
        description: fullShop.description,
        logoUrl: fullShop.logoUrl,
        coverImageUrl: fullShop.coverImageUrl,
        themeColor: fullShop.themeColor,
        contactEmail: fullShop.contactEmail,
        contactPhone: fullShop.contactPhone,
        defaultLocale: fullShop.defaultLocale,
        heroStyle: fullShop.heroStyle,
        storefrontType,
      });
      toast.success(t("saved"));
      setStep(3);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : tCommon("saveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSkip() {
    markOnboarded();
    navigate("/", { replace: true });
  }

  function handleFinish(target: "events" | "home") {
    markOnboarded();
    navigate(target === "events" ? "/events/new" : "/", { replace: true });
  }

  return (
    <div className="min-h-screen bg-muted/40 px-4 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          <span>{t("welcome", { name: me.data.companyName })}</span>
        </div>

        <ol className="mb-8 grid grid-cols-3 gap-2 text-xs">
          {([1, 2, 3] as const).map((n) => (
            <li
              key={n}
              className={cn(
                "rounded-md border px-3 py-2",
                step >= n
                  ? "border-primary/40 bg-primary/5 text-foreground"
                  : "bg-background text-muted-foreground",
              )}
            >
              <span className="font-mono">{n}.</span> {t(`steps.${STEP_KEYS[n]}`)}
            </li>
          ))}
        </ol>

        {step === 1 ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("step1Title")}</CardTitle>
              <CardDescription>{t("step1Description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {STOREFRONT_TYPES.map((type) => (
                <TypeOption
                  key={type}
                  title={t(`types.${type}.title`)}
                  description={t(`types.${type}.description`)}
                  selected={storefrontType === type}
                  onSelect={() => setStorefrontType(type)}
                />
              ))}
              <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" onClick={handleSkip}>
                  {t("skip")}
                </Button>
                <Button onClick={() => setStep(2)}>
                  {t("next")}
                  <ArrowRight className="ms-1.5 h-4 w-4 rtl:-scale-x-100" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {step === 2 ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("step2Title")}</CardTitle>
              <CardDescription>
                {t("shopLabel")} <span className="font-medium">{fullShop.name}</span>{" "}
                <span className="font-mono text-xs text-muted-foreground">
                  /{fullShop.slug}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border bg-background p-4 text-sm">
                <p className="mb-2 text-muted-foreground">{t("selectedType")}</p>
                <p className="text-base font-semibold">{t(`types.${storefrontType}.title`)}</p>
                <p className="mt-2 text-xs text-muted-foreground">{t("changeLater")}</p>
              </div>
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(1)}>
                  {tCommon("back")}
                </Button>
                <Button onClick={handleConfirm} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="me-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="me-1.5 h-4 w-4" />
                  )}
                  {tCommon("save")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {step === 3 ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("step3Title")}</CardTitle>
              <CardDescription>{t("step3Description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full" onClick={() => handleFinish("events")}>
                <CalendarPlus className="me-1.5 h-4 w-4" />
                {t("createEvent")}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => handleFinish("home")}
              >
                {t("goHome")}
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function TypeOption({
  title,
  description,
  selected,
  onSelect,
}: {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-3 rounded-md border p-4 text-start transition",
        selected
          ? "border-primary bg-primary/5"
          : "border-input hover:border-primary/40",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
        )}
      >
        {selected ? <Check className="h-3 w-3" /> : null}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-1 block text-xs text-muted-foreground">
          {description}
        </span>
      </span>
    </button>
  );
}
